import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Sparkles } from 'lucide-react';

const KINDS = [
  { key: 'skills', label: 'Skills', desc: 'Tag-based skill catalog used in Person profiles + matching.' },
  { key: 'tools', label: 'Tools', desc: 'Software / hardware the team uses (Blender, Maya, Nuke, etc.).' },
  { key: 'departments', label: 'Departments', desc: 'Organizational departments used for filtering and reporting.' },
  { key: 'employment_types', label: 'Employment Types', desc: 'employee · freelancer · vendor · intern.' },
];

export default function HRVocabularies() {
  const { api, user } = useAuth();
  const nav = useNavigate();
  const [vocab, setVocab] = useState(null);
  const [draft, setDraft] = useState({});
  const [activeKind, setActiveKind] = useState('skills');
  const [saving, setSaving] = useState(false);

  const isAllowed = user?.role === 'master_admin' || user?.role === 'hr_manager';
  useEffect(() => { if (!isAllowed) nav('/hr'); }, [isAllowed, nav]);

  useEffect(() => {
    api.get('/hr/vocabularies').then(r => {
      setVocab(r.data);
      setDraft({
        skills: [...(r.data.skills || [])],
        tools: [...(r.data.tools || [])],
        departments: [...(r.data.departments || [])],
        employment_types: [...(r.data.employment_types || [])],
      });
    });
  }, [api]);

  if (!vocab) return <div className="text-zinc-500 text-sm py-8 text-center" data-testid="vocab-loading">Loading vocabularies…</div>;

  const items = draft[activeKind] || [];
  const addItem = () => {
    const v = window.prompt('Add new item:');
    if (!v) return;
    setDraft(d => ({ ...d, [activeKind]: [...(d[activeKind] || []), v.trim()] }));
  };
  const removeItem = (i) => setDraft(d => ({ ...d, [activeKind]: d[activeKind].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/hr/vocabularies/${activeKind}`, { items: draft[activeKind] });
      const r = await api.get('/hr/vocabularies');
      setVocab(r.data);
      alert('Saved');
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="hr-vocab-page">
      <button onClick={() => nav('/hr')} className="flex items-center gap-1 text-[10px] text-zinc-500 hover:text-white">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to HR
      </button>
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-cyan-400" />
        <h1 className="text-2xl font-heading font-bold text-white">Vocabulary Catalog</h1>
        <span className="px-2 py-0.5 text-[9px] uppercase tracking-wider bg-cyan-500/15 text-cyan-300 rounded">Admin · HR</span>
      </div>
      <p className="text-xs text-zinc-500 -mt-3">Standardize all dropdowns. No more free-text entry — every Person profile picks from these lists.</p>

      <div className="flex gap-1 border-b border-zinc-800">
        {KINDS.map(k => (
          <button key={k.key} onClick={() => setActiveKind(k.key)} data-testid={`vocab-tab-${k.key}`}
            className={`px-4 py-2.5 text-xs font-medium border-b-2 -mb-px ${activeKind === k.key ? 'border-cyan-500 text-cyan-300' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            {k.label} <span className="ml-1 text-zinc-600">{(draft[k.key] || []).length}</span>
          </button>
        ))}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
        <p className="text-[10px] text-zinc-500 mb-3">{KINDS.find(k => k.key === activeKind)?.desc}</p>
        <div className="flex flex-wrap gap-2 mb-4" data-testid={`vocab-items-${activeKind}`}>
          {items.map((it, i) => (
            <span key={i} className="px-2 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded-sm text-[11px] text-cyan-200 flex items-center gap-1.5">
              {it}
              <button onClick={() => removeItem(i)} data-testid={`vocab-remove-${i}`} className="hover:text-rose-400">
                <Trash2 className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
          <button onClick={addItem} data-testid="vocab-add"
            className="px-2 py-1 bg-zinc-950 border border-dashed border-zinc-700 rounded-sm text-[11px] text-zinc-400 hover:border-cyan-500/50 hover:text-cyan-300 flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        <button onClick={save} disabled={saving} data-testid="vocab-save"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 rounded-sm text-xs text-white font-medium">
          <Save className="w-3 h-3" /> {saving ? 'Saving…' : `Save ${activeKind.replace('_', ' ')}`}
        </button>
      </div>
    </div>
  );
}
